import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow } from "./service/Workflow";
import { WorkflowUtil } from "./workflow.util";
import { S25EditableTextComponent } from "../s25-editable/s25-editable-text/s25.editable.text.component";

@TypeManagerDecorator("workflow-stage-transitions")
@Component({
    selector: "workflow-stage-transitions",
    template: `
        <div class="transitions-container">
            <div class="transitions-header">
                <h3>Transitions</h3>
                <button class="btn btn-primary" (click)="addTransition()">Add Transition</button>
            </div>

            <div class="transitions-list">
                @for (
                    transition of workflowProcess.stageTransitions;
                    track transition.workflowStageTransitionId || transition.id
                ) {
                    <div class="transition-item">
                        <div class="transition-main">
                            <div class="transition-info">
                                <div class="transition-flow">
                                    <select
                                        [(ngModel)]="transition.fromStageName"
                                        (ngModelChange)="onFromStageChange(transition, $event)"
                                        class="stage-select"
                                    >
                                        <option value="">From stage...</option>
                                        @for (stage of availableFromStages(); track stage.workflowStageId || stage.id) {
                                            <option [value]="stage.stageName">
                                                {{ stage.stageName }}{{ stage.isTerminal ? " (Terminal)" : "" }}
                                            </option>
                                        }
                                    </select>

                                    <div class="transition-arrow">
                                        <select [(ngModel)]="transition.transitionType" class="type-select">
                                            <option value="APPROVE">Approve</option>
                                            <option value="DENY">Deny</option>
                                        </select>
                                    </div>

                                    <select
                                        [(ngModel)]="transition.toStageName"
                                        (ngModelChange)="onToStageChange(transition, $event)"
                                        class="stage-select"
                                    >
                                        <option value="">To stage...</option>
                                        @for (
                                            stage of availableToStages(transition);
                                            track stage.workflowStageId || stage.id
                                        ) {
                                            <option [value]="stage.stageName">
                                                {{ stage.stageName }}{{ stage.isTerminal ? " (Terminal)" : "" }}
                                            </option>
                                        }
                                    </select>
                                </div>

                                <div class="transition-label">
                                    <s25-ng-editable-text
                                        [val]="transition.transitionLabel"
                                        [hasCommit]="true"
                                        [hasCommitButton]="true"
                                        [hasCancelButton]="true"
                                        [customValidation]="getTransitionLabelValidation(transition)"
                                        [onCommit]="getTransitionLabelCommitHandler(transition)"
                                        placeholder="Transition label"
                                        class="label-input"
                                    ></s25-ng-editable-text>
                                </div>
                            </div>
                            <div class="transition-actions">
                                <button class="btn btn-sm" (click)="toggleTransitionCollapse(transition)">
                                    {{ isTransitionCollapsed(transition) ? "▼" : "▲" }}
                                </button>
                                <button class="btn btn-sm btn-danger" (click)="removeTransition(transition)">×</button>
                            </div>
                        </div>

                        @if (!isTransitionCollapsed(transition)) {
                            <div class="transition-details">
                                <div class="transition-description">
                                    <textarea
                                        [(ngModel)]="transition.description"
                                        placeholder="Description (optional)"
                                        rows="2"
                                    ></textarea>
                                </div>

                                @if (getTransitionValidationErrors(transition).length > 0) {
                                    <div class="validation-errors">
                                        @for (error of getTransitionValidationErrors(transition); track error) {
                                            <div class="error-message">{{ error }}</div>
                                        }
                                    </div>
                                }
                            </div>
                        }
                    </div>
                }
            </div>

            @if ((workflowProcess.stageTransitions || []).length === 0) {
                <div class="no-transitions">
                    <p>No transitions defined. Add transitions to connect your workflow stages.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .transitions-container {
            padding: 20px;
        }

        .transitions-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }

        .transitions-header h3 {
            margin: 0;
            color: #333;
        }

        .btn {
            padding: 8px 16px;
            border: 1px solid #ddd;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            transition: all 0.2s;
        }

        .btn-primary {
            background: #007bff;
            color: white;
            border-color: #007bff;
        }

        .btn-primary:hover {
            background: #0056b3;
        }

        .btn-sm {
            padding: 4px 8px;
            font-size: 12px;
        }

        .btn-danger {
            background: #dc3545;
            color: white;
            border-color: #dc3545;
        }

        .btn-danger:hover {
            background: #c82333;
        }

        .transitions-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .transition-item {
            background: white;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            overflow: hidden;
        }

        .transition-main {
            padding: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: #f8f9fa;
        }

        .transition-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 12px;
        }

        .transition-flow {
            display: flex;
            align-items: center;
            gap: 12px;
        }

        .stage-select {
            flex: 1;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
        }

        .transition-arrow {
            display: flex;
            align-items: center;
        }

        .type-select {
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            background: #007bff;
            color: white;
            font-weight: 500;
        }

        .transition-label {
            margin-top: 8px;
        }

        .label-input {
            width: 100%;
            font-weight: 500;
            color: #333;
        }

        .transition-actions {
            display: flex;
            gap: 8px;
        }

        .transition-details {
            padding: 20px;
            border-top: 1px solid #e0e0e0;
        }

        .transition-description {
            margin-bottom: 16px;
        }

        .transition-description textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            resize: vertical;
        }

        .validation-errors {
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            border-radius: 4px;
            padding: 12px;
        }

        .error-message {
            color: #856404;
            font-size: 14px;
            margin-bottom: 4px;
        }

        .error-message:last-child {
            margin-bottom: 0;
        }

        .no-transitions {
            text-align: center;
            padding: 40px 20px;
            color: #666;
        }
    `,
    standalone: true,
    imports: [CommonModule, FormsModule, S25EditableTextComponent],
})
export class WorkflowStageTransitionsComponent implements OnInit {
    @Input() workflowProcess!: Workflow.WorkflowProcess;
    @Output() transitionsChange = new EventEmitter<Workflow.WorkflowStageTransition[]>();

    private collapsedTransitions = new Set<number | string>();

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit() {
        if (!this.workflowProcess.stageTransitions) {
            this.workflowProcess.stageTransitions = [];
        }
        // Use the utility function to populate stage names
        WorkflowUtil.populateStageNamesOnTransitions(
            this.workflowProcess.stageTransitions,
            this.workflowProcess.stages || [],
        );
    }

    addTransition() {
        const newTransitionTempId =
            (this.workflowProcess.stageTransitions || []).reduce(
                (minId, transition) => Math.min(minId, transition.id || -1),
                -1,
            ) - 1;

        const newTransition: Workflow.WorkflowStageTransition = {
            id: newTransitionTempId,
            workflowProcessId: this.workflowProcess.workflowProcessId,
            transitionType: "APPROVE",
            transitionLabel: "",
            fromStageName: "",
            toStageName: "",
            description: "",
        };

        if (!this.workflowProcess.stageTransitions) {
            this.workflowProcess.stageTransitions = [];
        }
        this.workflowProcess.stageTransitions.push(newTransition);
        this.emitTransitionsChange();
    }

    removeTransition(transitionToRemove: Workflow.WorkflowStageTransition) {
        if (!this.workflowProcess.stageTransitions) return;

        const index = this.workflowProcess.stageTransitions.findIndex(
            (transition) =>
                (transition.workflowStageTransitionId || transition.id) ===
                (transitionToRemove.workflowStageTransitionId || transitionToRemove.id),
        );
        if (index !== -1) {
            this.workflowProcess.stageTransitions.splice(index, 1);
            this.emitTransitionsChange();
        }
    }

    onFromStageChange(transition: Workflow.WorkflowStageTransition, stageName: string) {
        transition.fromStageName = stageName;

        // Find the stage and set the fromStageId if it exists
        const stage = this.workflowProcess.stages?.find((s) => s.stageName === stageName);
        if (stage?.workflowStageId) {
            transition.fromStageId = stage.workflowStageId;
        }

        this.emitTransitionsChange();
    }

    onToStageChange(transition: Workflow.WorkflowStageTransition, stageName: string) {
        transition.toStageName = stageName;

        // Find the stage and set the toStageId if it exists
        const stage = this.workflowProcess.stages?.find((s) => s.stageName === stageName);
        if (stage?.workflowStageId) {
            transition.toStageId = stage.workflowStageId;
        }

        this.emitTransitionsChange();
    }

    availableFromStages() {
        // Return all stages for from selection except terminal stages
        return (this.workflowProcess.stages || []).filter((s) => !s.isTerminal);
    }

    availableToStages(transition: Workflow.WorkflowStageTransition) {
        // Return all stages except the one being transitioned from
        return (this.workflowProcess.stages || []).filter((s) => s.stageName !== transition.fromStageName);
    }

    getTransitionValidationErrors(transition: Workflow.WorkflowStageTransition): string[] {
        return WorkflowUtil.getTransitionValidationErrors(
            this.workflowProcess.stageTransitions || [],
            this.workflowProcess.stages || [],
            transition,
        );
    }

    toggleTransitionCollapse(transition: Workflow.WorkflowStageTransition) {
        const id = transition.workflowStageTransitionId || transition.id;
        if (this.collapsedTransitions.has(id)) {
            this.collapsedTransitions.delete(id);
        } else {
            this.collapsedTransitions.add(id);
        }
    }

    isTransitionCollapsed(transition: Workflow.WorkflowStageTransition): boolean {
        const id = transition.workflowStageTransitionId || transition.id;
        return this.collapsedTransitions.has(id);
    }

    getTransitionLabelCommitHandler(transition: Workflow.WorkflowStageTransition) {
        return (newName: string) => {
            this.onTransitionLabelCommit(transition, newName);
        };
    }

    onTransitionLabelCommit(transition: Workflow.WorkflowStageTransition, newLabel: string) {
        transition.transitionLabel = newLabel;
        this.emitTransitionsChange();
    }

    getTransitionLabelValidation(transition: Workflow.WorkflowStageTransition) {
        return (inputVal: string) => {
            const trimmedLabel = inputVal?.trim();

            if (!trimmedLabel) {
                return ["Transition label is required"];
            }

            // Check for duplicate transition labels
            const isDuplicate = this.workflowProcess.stageTransitions.some(
                (t) =>
                    t.transitionLabel === trimmedLabel &&
                    t !== transition &&
                    (t.workflowStageTransitionId !== transition.workflowStageTransitionId || t.id !== transition.id),
            );

            if (isDuplicate) {
                return [`Transition label "${trimmedLabel}" already exists. Please choose a unique name.`];
            }

            return true;
        };
    }

    private emitTransitionsChange() {
        this.cd.detectChanges();
        this.transitionsChange.emit(this.workflowProcess.stageTransitions || []);
    }
}
